import { Media } from '@/components/Media'
import { useParams } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { formatMoney, fromMajor, money } from '@/lib/money'
import { findFlight, FLIGHT_CASHBACK } from './travel-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import { BRAND } from '@/brand/brand'

const RULES = [
  ['Change', 'Allowed · fare difference applies'],
  ['Cancel', 'Refund as credit up to 24h after booking'],
  ['Seat', 'Chosen at checkout'],
  ['Baggage', 'Shown below, per airline rules'],
] as const

export function FlightDetailScreen() {
  const { id = '' } = useParams()
  const flight = findFlight(id)
  if (!flight) return <NotFoundScreen />

  const fare = fromMajor(flight.fare, 'USD')
  const taxes = fromMajor(flight.taxes, 'USD')
  const total = money(fare.minorUnits + taxes.minorUnits, 'USD')
  const cashback = money(Math.round(total.minorUnits * FLIGHT_CASHBACK), 'USD')
  const instalment = money(Math.round(total.minorUnits / 4), 'USD')

  return (
    <>
      <PageHeader
        title={flight.route}
        subtitle={`${flight.airline} · ${flight.time} · ${flight.cabin}`}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="arrow_back" to="/travel/flights">
              All flights
            </Button>
            <Button variant="ghost" size="md" iconStart="notifications_active">
              Fare alert
            </Button>
          </>
        }
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Media
            src={flight.image}
            alt={`Where ${flight.route} lands`}
            ratio={16 / 9}
            radius="lg"
            icon="flight_takeoff"
            eager
          />

          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">
                Your itinerary
              </Text>
              <Pill tone="neutral">
                {flight.legs.length === 1 ? 'Direct' : `${flight.legs.length - 1} stop`}
              </Pill>
            </Card.Header>
            <Stack gap={0} className="pb-mt-3">
              {flight.legs.map((leg) => (
                <div key={leg.from + leg.depart} className={checkout.line}>
                  <Icon name="flight_takeoff" size={20} />
                  <Stack grow gap={1} align="start">
                    <Text variant="caption" weight="semibold">
                      {leg.from} → {leg.to}
                    </Text>
                    <Text variant="caption" tone="secondary">
                      {leg.depart} → {leg.arrive} · {leg.aircraft}
                    </Text>
                  </Stack>
                  <Text variant="caption" weight="semibold" numeric>
                    {leg.duration}
                  </Text>
                </div>
              ))}
            </Stack>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              What the fare includes
            </Text>
            <Stack gap={0} className="pb-mt-3">
              <div className={checkout.line}>
                <Icon name="luggage" size={20} />
                <Stack grow gap={0} align="start">
                  <Text variant="caption" weight="semibold">
                    Baggage
                  </Text>
                  <Text variant="caption" tone="secondary">
                    {flight.bag}
                  </Text>
                </Stack>
              </div>
              {RULES.map(([label, value]) => (
                <div key={label} className={checkout.line}>
                  <Text variant="caption" tone="secondary">
                    {label}
                  </Text>
                  <Text variant="caption" weight="semibold">
                    {value}
                  </Text>
                </div>
              ))}
            </Stack>
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">
                One way, one passenger
              </Text>
              <Pill tone="accent">1% back</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-3">
              {formatMoney(total)}
            </Text>
            <div className={checkout.rule} />
            {[
              ['Base fare', formatMoney(fare)],
              ['Taxes and charges', formatMoney(taxes)],
              ['Booking fee', 'None'],
              ['Four payments of', formatMoney(instalment)],
              ['Cashback', `+${formatMoney(cashback)}`],
            ].map(([label, value], index) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text
                  variant="caption"
                  weight="semibold"
                  tone={index === 2 || index === 4 ? 'accent' : 'onDark'}
                  numeric
                >
                  {value}
                </Text>
              </div>
            ))}
            <Card.Footer>
              <Button
                size="lg"
                block
                iconEnd="arrow_forward"
                to={`/travel/flights/checkout?flight=${flight.slug}`}
              >
                Choose seat and pay
              </Button>
              <Stack direction="row" align="center" gap={2} className="pb-mt-3">
                <Icon name="info" size={15} />
                <Text variant="eyebrow" tone="onDarkMuted">
                  Charged in dollars from your {BRAND.name} balance, with no FX fee.
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </Stack>
      </div>
    </>
  )
}
